import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EstablishmentService } from '../../establishment.service';
import { UserProfileService } from '../../../user-profile/user-profile.service';
import icSearch from '@iconify/icons-ic/twotone-search';
import icClose from '@iconify/icons-ic/twotone-close';
import icPersonAdd from '@iconify/icons-ic/twotone-person-add';
import icArchive from '@iconify/icons-ic/twotone-archive';
import icUnarchive from '@iconify/icons-ic/twotone-unarchive';
import icVisibility from '@iconify/icons-ic/twotone-visibility';
import icVisibilityOff from '@iconify/icons-ic/twotone-visibility-off';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentContactStepperComponent } from '../../modals/establishment-contact-stepper/establishment-contact-stepper.component';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { EstablishmentTypeEnum } from '../../../../../../core/enums/establishment-type.enum';
import { SupplierContactCreateUpdateDeleteComponent } from '../../../supplier/modals/supplier-contact-create-update-delete/supplier-contact-create-update-delete.component';
import { FormModalDeleteBaseComponent } from '../../../../../../core/base/components/form-modal-delete-base/form-modal-delete-base.component';
import { EstablishmentContactAffectationComponent } from '../../modals/establishment-contact-affectation/establishment-contact-affectation.component';
import { AuthService } from '../../../../../../core/services/auth.service';

@Component({
  selector: 'vex-establishment-details-contact',
  templateUrl: './establishment-details-contact.component.html',
  styleUrls: ['./establishment-details-contact.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class EstablishmentDetailsContactComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscription = new Subscription();

  loading = false;
  icClose = icClose;
  icSearch = icSearch;
  icPersonAdd = icPersonAdd;
  icArchive = icArchive;
  icUnarchive = icUnarchive;
  icVisibility = icVisibility;
  icVisibilityOff = icVisibilityOff;
  establishment: EstablishmentModel;
  filteredContacts$: Observable<any[]>;
  showArchived = false;

  @ViewChild('search') search: ElementRef;

  ENUM_PERMISSION = ENUM_PERMISSIONS;

  constructor(
    public establishmentService: EstablishmentService,
    public userProfileService: UserProfileService,
    private dialog: MatDialog,
    private router: Router,
    private snackbarService: SnackbarService,
    public authService: AuthService,
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.establishmentService.entity$
        .pipe(
          distinctUntilChanged((prev, curr) => prev?._id === curr?._id),
          tap(entity => {
            this.establishment = entity;
            this.setFilteredContacts('');
          }),
        )
        .subscribe(),
    );

    // Initialisation de l'observable; sera remplacée par setFilteredContacts
    // this.filteredContacts$ = of([]);
  }

  ngAfterViewInit() {
    this.subscription.add(
      fromEvent(this.search.nativeElement, 'keyup')
        .pipe(
          map(value => {
            return value;
          }),
          debounceTime(500),
          distinctUntilChanged(),
          tap(value => {
            this.setFilteredContacts(this.search.nativeElement.value);
          }),
        )
        .subscribe(),
    );
  }

  initData() {
    this.setFilteredContacts('');
  }

  private refreshContacts() {
    this.setFilteredContacts(this.search?.nativeElement?.value || '');
  }

  clearTextFilter() {
    this.search.nativeElement.value = '';
    this.initData();
  }

  private setFilteredContacts(search: string) {
    const isPredikUser = !!this.authService.checkCurrentIsPredik();

    if (this.establishment?.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
      if (this.showArchived) {
        // Afficher les contacts internes archivés
        this.filteredContacts$ = this.establishmentService.getArchivedInternalContacts(this.establishment._id).pipe(
          map(x => x.data || []),
          map(contacts => {
            console.log('Archived contacts from API:', contacts);
            // S'assurer que fullName et isEstablishmentContact sont définis
            const mappedContacts = contacts.map(c => ({
              ...c,
              fullName: `${c.firstName} ${c.lastName}`,
              isEstablishmentContact: false,
            }));

            if (search) {
              const term = search.toLowerCase();
              // Normaliser le terme de recherche pour les téléphones
              const searchPhoneNormalized = search.replace(/[\s\-\.\(\)]/g, '');
              return mappedContacts.filter(c => {
                const email = c.email?.toLowerCase().includes(term);
                const fullName = c.fullName.toLowerCase().includes(term);
                const fullNameReverse = `${c.lastName} ${c.firstName}`.toLowerCase().includes(term);
                const phone = c.phoneNumbers?.some(p => {
                  const phoneNormalized = p.phoneNumber?.replace(/[\s\-\.\(\)]/g, '') || '';
                  const displayNormalized = p.displayPhoneNumber?.replace(/[\s\-\.\(\)]/g, '') || '';
                  return (
                    phoneNormalized.includes(searchPhoneNormalized) || displayNormalized.includes(searchPhoneNormalized)
                  );
                });
                return email || fullName || fullNameReverse || phone;
              });
            }
            console.log('Final mapped archived contacts:', mappedContacts);
            return mappedContacts;
          }),
          catchError(() => of([])),
          shareReplay(1),
        );
      } else {
        // Pour les établissements support, on doit afficher AUSSI les contacts internes
        // On doit récupérer les contacts internes ET les contacts d'établissement

        // Pour les établissements support, récupérer uniquement les contacts internes actifs
        // (les contacts d'établissement externes ne sont pas gérés pour les SUPPORT_FUNCTION)
        this.filteredContacts$ = this.establishmentService
          .getActiveInternalContacts(this.establishment._id, search, ['isPredik'])
          .pipe(
            map(x => {
              console.log('Active contacts from API:', x.data);
              const internalContacts = x.data.map(c => ({
                ...c,
                isEstablishmentContact: false,
                fullName: `${c.firstName} ${c.lastName}`,
              }));

              // Filtrer les contacts Predik si nécessaire
              const filteredInternalContacts = isPredikUser
                ? internalContacts
                : internalContacts.filter(c => !c.isPredik);

              console.log('Final filtered contacts for active view:', filteredInternalContacts);
              return filteredInternalContacts;
            }),
            catchError(() => of([])),
            shareReplay(1),
          );
      }
      return;
    }

    // Contacts internes: on demande explicitement isPredik et contactType via requiredFields
    this.filteredContacts$ = this.userProfileService
      .findAll(null, null, 'firstName', 'asc', search, {
        establishment: this.establishment._id,
        requiredFields: ['isPredik', 'contactType'],
      })
      .pipe(
        map(x => x.data.map(c => ({ ...c, isEstablishmentContact: false }))),
        map(list => (isPredikUser ? list : list.filter(c => !c.isPredik))),
        catchError(() => of([])),
        shareReplay(1),
      );
  }

  // addContact() {
  //     console.log("ca va mec");
  //     const sub = this.dialog.open(EstablishmentContactAffectationComponent, {
  //         data: {
  //             defaults: this.establishment,
  //             mode: CrudModeEnum.Create
  //         },
  //         width: '800px'
  //     }).afterClosed().subscribe((res) => {
  //         if (res) {
  //             this.initData();
  //         }
  //     });
  //     this.subscription.add(sub);
  // }

  addContact() {
    const sub = this.dialog
      .open(EstablishmentContactAffectationComponent, {
        data: {
          defaults: this.establishment,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe(createdContact => {
        if (createdContact) {
          // Recharger les contacts après l'ajout
          this.initData();
        }
      });
    this.subscription.add(sub);
  }

  deleteContact(contact: any) {
    if (contact.isEstablishmentContact) {
      const sub = this.dialog
        .open(SupplierContactCreateUpdateDeleteComponent, {
          data: {
            establishmentId: this.establishment._id,
            defaults: contact,
            mode: CrudModeEnum.Delete,
            name: `${contact.firstName} ${contact.lastName}`,
          },
          width: '800px',
        })
        .afterClosed()
        .subscribe(res => {
          if (res) {
            this.initData();
          }
        });
      this.subscription.add(sub);
      return;
    }

    const ref = this.dialog.open(FormModalDeleteBaseComponent, {
      data: {
        label: 'cette affectation',
        defaultMessage: `Êtes-vous sûr de vouloir supprimer cette affectation ?`,
      },
    });

    const instance = ref.componentInstance;

    const closeSub = instance.onClose.subscribe(() => ref.close(false));
    const deleteSub = instance.onDelete.subscribe(() => {
      instance.loading = true;

      // Le contrôle métier « acteur achat » est effectué côté back par deleteAffectationByEstablishment
      // (PRED-3206) : le snackbar d'erreur est affiché automatiquement par ErrorCrudInterceptor.
      this.userProfileService.deleteAffectationByEstablishmentId(contact._id, this.establishment._id).subscribe({
        next: () => {
          instance.loading = false;
          ref.close(true);
        },
        error: () => (instance.loading = false),
      });
    });

    ref.afterClosed().subscribe(result => {
      closeSub.unsubscribe();
      deleteSub.unsubscribe();
      if (result) {
        this.initData();
      }
    });
  }

  editContact(contact: any) {
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          establishmentId: this.establishment._id,
          defaults: { ...contact },
          mode: CrudModeEnum.Update,
          fromEstablishmentDetails: true,
          contactTypeOnly: this.establishment.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION,
        },
        width: '800px',
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.initData();
        }
      });
    this.subscription.add(sub);
  }

  openContact(contact: any) {
    this.router.navigate([`/management/user-profile/${contact._id}`]);
  }

  toggleArchivedView() {
    this.showArchived = !this.showArchived;
    console.log('Toggle archived view:', this.showArchived);
    this.setFilteredContacts(this.search?.nativeElement?.value || '');
  }

  archiveContact(contact: any) {
    if (!contact._id || !this.establishment?._id) return;

    const ref = this.dialog.open(FormModalDeleteBaseComponent, {
      data: {
        title: 'Archiver le contact',
        defaultMessage: `Êtes-vous sûr de vouloir archiver le contact "${contact.fullName}" ?`,
      },
    });

    const instance = ref.componentInstance;

    const closeSub = instance.onClose.subscribe(() => ref.close(false));
    const confirmSub = instance.onDelete.subscribe(() => {
      instance.loading = true;

      this.establishmentService.archiveInternalContact(this.establishment._id, contact._id).subscribe({
        next: () => {
          instance.loading = false;
          ref.close(true);
          this.snackbarService.success('Contact interne archivé avec succès');

          // Refetch l'establishment pour avoir les données à jour
          this.establishmentService
            .findById(this.establishment._id)
            .pipe(
              tap(updatedEstab => {
                // Cette mise à jour va automatiquement déclencher le refresh
                // via la souscription de ngOnInit
              }),
            )
            .subscribe();
          this.refreshContacts();
        },
        error: error => {
          console.error("Erreur lors de l'archivage:", error);
          this.snackbarService.danger("Erreur lors de l'archivage du contact");
          instance.loading = false;
        },
      });
    });

    ref.afterClosed().subscribe(result => {
      closeSub.unsubscribe();
      confirmSub.unsubscribe();
    });
  }

  unarchiveContact(contact: any) {
    if (!contact._id || !this.establishment?._id) return;

    const ref = this.dialog.open(FormModalDeleteBaseComponent, {
      data: {
        title: 'Désarchiver le contact',
        defaultMessage: `Êtes-vous sûr de vouloir désarchiver le contact "${contact.fullName}" ?`,
      },
    });

    const instance = ref.componentInstance;

    const closeSub = instance.onClose.subscribe(() => ref.close(false));
    const confirmSub = instance.onDelete.subscribe(() => {
      instance.loading = true;

      this.establishmentService.unarchiveInternalContact(this.establishment._id, contact._id).subscribe({
        next: () => {
          instance.loading = false;
          ref.close(true);
          this.snackbarService.success('Contact interne désarchivé avec succès');

          // Refetch l'establishment pour avoir les données à jour
          this.establishmentService
            .findById(this.establishment._id)
            .pipe(
              tap(updatedEstab => {
                // Cette mise à jour va automatiquement déclencher le refresh
                // via la souscription de ngOnInit
              }),
            )
            .subscribe();

          // Si on était dans la vue archivés, retourner à la vue normale
          if (this.showArchived) {
            this.showArchived = false;
          }
          this.refreshContacts();
        },
        error: error => {
          console.error('Erreur lors du désarchivage:', error);
          this.snackbarService.danger('Erreur lors du désarchivage du contact');
          instance.loading = false;
        },
      });
    });

    ref.afterClosed().subscribe(result => {
      closeSub.unsubscribe();
      confirmSub.unsubscribe();
    });
  }

  isInternalContact(contact: any): boolean {
    return (
      this.establishment?.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION &&
      !contact.isEstablishmentContact
    );
  }

  canArchiveContact(contact: any): boolean {
    return this.isInternalContact(contact) && !this.showArchived;
  }

  canUnarchiveContact(contact: any): boolean {
    return this.isInternalContact(contact) && this.showArchived;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
